import { useState, useEffect, useCallback } from 'react';
import { favoritosService } from '@/services/favoritosService';
import { Property } from '@/integrations/mysql/types';
import { useAuthState } from './useAuthState';

export const useFavoritos = () => {
  const [favoritos, setFavoritos] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuthState();

  // Carregar favoritos do usuário
  const loadFavoritos = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setFavoritos([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userFavoritos = await favoritosService.getFavoritosProperties(user.id);
      setFavoritos(userFavoritos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar favoritos');
      console.error('Erro ao carregar favoritos:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Carregar favoritos quando o usuário mudar
  useEffect(() => {
    loadFavoritos();
  }, [loadFavoritos]);

  // Adicionar aos favoritos
  const addToFavoritos = useCallback(async (propertyId: number): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) {
      setError('Usuário não autenticado');
      return false;
    }

    try {
      const success = await favoritosService.addToFavoritos(user.id, propertyId);
      if (success) {
        // Recarregar lista de favoritos
        await loadFavoritos();
      }
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar aos favoritos');
      console.error('Erro ao adicionar aos favoritos:', err);
      return false;
    }
  }, [isAuthenticated, user?.id, loadFavoritos]);

  // Remover dos favoritos
  const removeFromFavoritos = useCallback(async (propertyId: number): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) {
      setError('Usuário não autenticado');
      return false;
    }

    try {
      const success = await favoritosService.removeFromFavoritos(user.id, propertyId);
      if (success) {
        // Atualizar lista local
        setFavoritos(prev => prev.filter(item => item.id !== propertyId));
      }
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover dos favoritos');
      console.error('Erro ao remover dos favoritos:', err);
      return false;
    }
  }, [isAuthenticated, user?.id]);

  // Verificar se está nos favoritos
  const isInFavoritos = useCallback((propertyId: number): boolean => {
    return favoritos.some(item => item.id === propertyId);
  }, [favoritos]);

  // Limpar lista de favoritos
  const clearFavoritos = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) {
      setError('Usuário não autenticado');
      return false;
    }

    try {
      const success = await favoritosService.clearFavoritos(user.id);
      if (success) {
        setFavoritos([]);
      }
      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao limpar favoritos');
      console.error('Erro ao limpar favoritos:', err);
      return false;
    }
  }, [isAuthenticated, user?.id]);

  return {
    favoritos,
    loading,
    error,
    addToFavoritos,
    removeFromFavoritos,
    isInFavoritos,
    clearFavoritos,
    loadFavoritos
  };
};